"use client"

import Image from "next/image"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Grid } from "react-loader-spinner"
import { toast } from "sonner"
import { Input } from "../ui/input"
import Button from "../ui/Button"
import { COLORS } from "@/styles/theme"
import { useJobsAlert } from "@/hooks/useJobsAlert"
import { useMauticTracking } from "@/hooks/useMauticTracking"
import { MAUTIC_CAMPAIGNS } from "@/lib/mautic-campaign-tags"
import { JobAlertFormData, JobAlertSchema } from "@/utils/helpers/validators/jobAlert/jobAlert.schema"

export default function JobAlerts() {
   const { createJobsAlertMutation } = useJobsAlert()
   const { trackPageView } = useMauticTracking()

   const form = useForm<JobAlertFormData>({
      resolver: zodResolver(JobAlertSchema),
      defaultValues: {
         email: "",
         email_frequency: "Daily",
      },
   })

   const onSubmit = (data: JobAlertFormData) => {
      createJobsAlertMutation.mutate(data, {
         onSuccess: () => {
            toast.success("Job alert created successfully!") // Fire Mautic tracking campaign upon successful mutation completion
            const c = MAUTIC_CAMPAIGNS.jobSubscriptionClaimed
            trackPageView({
               mt_event: c.mt_event,
               page_title: c.page_title,
               page_url: c.pageurl,
               tags: c.tags,
            }) // Reset form fields back to original layout defaults

            form.reset({ email: "", email_frequency: "Daily" })
         },
         onError: (error: Error) => {
            toast.error(error.message || "Failed to create job alert. Please try again.")
         },
      })
   }

   return (
      <section className="relative flex min-h-[250px] w-full max-w-[536px] flex-col items-center rounded-3xl bg-[#FFF5F1] p-10 text-center shadow-lg">
         {createJobsAlertMutation.isPending && (
            <div className="fixed inset-0 z-50 grid h-screen w-screen place-content-center bg-black/15">
               <Grid
                  visible={true}
                  height="80"
                  width="80"
                  color={COLORS.orange || "#FF7043"}
                  ariaLabel="grid-loading"
                  radius="12.5"
                  wrapperStyle={{ width: "100%", height: "100%" }}
                  wrapperClass="grid-wrapper"
               />
            </div>
         )}
         <div className="relative mb-8 h-24 w-full max-w-40">
            <Image
               src="/_images/ui/mail-alert.svg"
               alt="Job Alert Illustration"
               fill
               className="object-contain"
               priority
            />
         </div>
         <h2 className="mb-2 text-custom-3xl-bold ">
            Sign Up For <span className="text-[#FF7043]">Job Alerts</span>
         </h2>
         <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
            <div className="flex w-full items-start gap-4">
               <div className="relative w-full text-left">
                  <Input
                     type="email"
                     placeholder="Your email"
                     className="h-12 w-full rounded-xl border border-gray-100 bg-white px-5 text-gray-700 shadow-[0_2px_4px_rgba(0,0,0,0.02)] focus:border-[#FF7043] focus:ring-[#FF7043]"
                     {...form.register("email")}
                  />
                  {form.formState.errors.email?.message && (
                     <p className="mt-2 pl-1 text-xs font-medium text-red-500">{form.formState.errors.email.message}</p>
                  )}
               </div>
               <div className="relative w-full max-w-[140px] text-left">
                  <select
                     className="h-12 w-full rounded-xl border border-gray-100 bg-white px-5 pr-10 text-gray-700 shadow-[0_2px_4px_rgba(0,0,0,0.02)] outline-none transition focus:border-[#FF7043] focus:ring-[#FF7043]"
                     {...form.register("email_frequency")}
                  >
                     <option value="Daily">Daily</option>
                     <option value="Weekly">Weekly</option>
                     <option value="Monthly">Monthly</option>
                  </select>
                  <svg
                     width="12"
                     height="8"
                     viewBox="0 0 12 8"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                     aria-hidden="true"
                     className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#FF7043]"
                  >
                     <path
                        d="M11 1L6 6L1 1"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
                  {form.formState.errors.email_frequency?.message && (
                     <p className="mt-2 pl-1 text-xs font-medium text-red-500">
                        {form.formState.errors.email_frequency.message}
                     </p>
                  )}
               </div>
            </div>
            <Button
               type="submit"
               disabled={createJobsAlertMutation.isPending}
               className="mt-8 h-12 w-full rounded-xl bg-[#FF7043] text-xl font-bold text-white shadow-md transition-all hover:bg-[#F4511E] active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
            >
               Create Alert
            </Button>
         </form>
      </section>
   )
}
